import { useState, useEffect } from 'react';
import { MOBILE_APP_CONFIG } from '../config/mobileApp.config';

interface MobileAppDetection {
    isMobile: boolean;
    isIOS: boolean;
    isAndroid: boolean;
    userAgent: string;
    canOpenApp: boolean;
}

export const useMobileAppDetection = (): MobileAppDetection => {
    const [detection, setDetection] = useState<MobileAppDetection>({
        isMobile: false,
        isIOS: false,
        isAndroid: false,
        userAgent: '',
        canOpenApp: false,
    });

    useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();

        // 1. Tablet UA check: Android tablets omit "mobile", plus explicit iPad/Tablet strings
        const isTabletUA = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/i.test(userAgent);

        // 2. Screen size check: Screen width <= 768px and max screen dimension <= 950px
        // Helps catch modern iPads/tablets (even when requesting desktop sites)
        const isPhoneScreen = window.innerWidth <= 768 && Math.max(window.screen.width, window.screen.height) <= 950;

        // 3. User agent check specifically targeting handheld phone platforms
        const isPhoneUA = /iphone|ipod|android.*mobile|blackberry|iemobile|opera mini/i.test(userAgent);

        // Final phone classification: Must have a phone UA, match screen size, and NOT be a tablet
        const isHandheldPhone = isPhoneUA && isPhoneScreen && !isTabletUA;

        // Platform breakdown specifically for handheld phones
        const isIOS = /iphone|ipod/i.test(userAgent) && isHandheldPhone;
        const isAndroid = /android/i.test(userAgent) && /mobile/i.test(userAgent) && isHandheldPhone;

        const canOpenApp = isHandheldPhone && (isIOS || isAndroid);

        setDetection({
            isMobile: isHandheldPhone, // Now strictly true ONLY for handheld phones
            isIOS,
            isAndroid,
            userAgent,
            canOpenApp,
        });
    }, []);

    return detection;
};

// Deep link URLs for the mobile app
export const APP_DEEP_LINKS = MOBILE_APP_CONFIG.deepLinks;

// App store URLs
export const APP_STORE_URLS = MOBILE_APP_CONFIG.appStores;

// Universal link for iOS (if you have one)
export const UNIVERSAL_LINK = MOBILE_APP_CONFIG.universalLink;
